import { ChevronRightOutlineIcon, TimerOutlineIcon } from '@ikyucom/design-system-icons-react'
import type { ComponentProps } from 'react'
import { LabelNormal } from '~/components/LabelNormal'
import { PriceRange } from '~/components/restaurant/PriceRange'
import { FavoriteRestaurantButton } from '~/features/favorite/restaurant'
import { ShareRestaurantButton } from '~/features/share/ShareRestaurantButton'
import { makeAccessText } from '~/lib/accesses/accesses'
import { type FragmentType, getFragmentData, graphql } from '~/lib/graphql'
import { useIsPC } from '~/lib/ua'
import { Link } from '~/navigation'
import { sva } from '~/styled-system/css'
import { hstack, vstack } from '~/styled-system/patterns'
import { GuideIkyuDayBanner } from './GuideIkyuDayBanner'
import InventoryNotificationComponent from './InventoryNotification'

const RestaurantGuideTitle = graphql(`
  fragment RestaurantGuideTitle on Restaurant {
    restaurantId
    name
    isCasual
    area {
      largeCode
    }
    genre {
      displayName
    }
  }
`)

type Props = {
  restaurant: FragmentType<typeof RestaurantGuideTitle> &
    Parameters<typeof makeAccessText>[0] &
    ComponentProps<typeof PriceRange>['restaurant'] &
    ComponentProps<typeof ShareRestaurantButton>['restaurant']
  visibleTimeSaleLabel?: boolean
  showIkyuDayBanner?: boolean
  showInventoryNotification?: boolean
}

const styles = sva({
  slots: [
    'root',
    'title',
    'genre',
    'restaurantName',
    'subtitle',
    'supplement',
    'access',
    'accessText',
    'chevron',
    'timesale',
    'timesaleIcon',
    'buttons',
  ],
  base: {
    root: {
      width: 'full',
      margin: '0',
      _pc: {
        maxWidth: 'content.base',
        paddingTop: '6',
        paddingBottom: '5',
        margin: 'auto',
      },
    },
    title: vstack.raw({
      gap: '1',
      padding: '3',
      alignItems: 'start',
      width: 'full',
      _pc: {
        gap: '2',
        padding: '0',
      },
    }),
    genre: {
      color: 'gray.700',
      fontSize: 'xs',
      _pc: {
        fontSize: 'md',
      },
    },
    restaurantName: {
      mt: '1',
      fontSize: '2xl',
      fontWeight: 'bold',
      _pc: {
        mt: '0',
        fontSize: '5xl',
      },
    },
    subtitle: hstack.raw({
      gap: '3',
      justifyContent: 'space-between',
      alignItems: 'end',
      width: 'full',
      _pc: {
        gap: '5',
      },
    }),
    supplement: vstack.raw({
      gap: '2',
      width: '2/3',
      flex: '1',
      alignItems: 'start',
      margin: 'auto',
    }),
    access: hstack.raw({
      gap: '0',
      alignItems: 'center',
      fontSize: 'sm',
      maxWidth: 'full',
      _pc: {
        fontSize: 'md',
      },
    }),
    accessText: {
      truncate: true,
    },
    chevron: {
      color: 'link',
      flexShrink: 0,
      _pc: {
        color: 'primary',
      },
    },
    timesale: {
      mt: '1',
      _pc: {
        mt: '0',
      },
    },
    timesaleIcon: {
      color: 'timesale.border',
      flexShrink: 0,
      mr: '1',
    },
    buttons: hstack.raw({
      gap: '2',
      alignItems: 'end',
    }),
  },
})

const ikyu = import.meta.env.VITE_MODE === 'ikyu'

export function GuideTitle({
  restaurant,
  visibleTimeSaleLabel,
  showIkyuDayBanner = false,
  showInventoryNotification = false,
}: Props) {
  const {
    restaurantId,
    name: restaurantName,
    isCasual,
    area: { largeCode },
    genre: { displayName: genre },
  } = getFragmentData(RestaurantGuideTitle, restaurant)
  const accessText = makeAccessText(restaurant)
  const classes = styles()
  const isPC = useIsPC()

  return (
    <div className={classes.root}>
      <div className={classes.title}>
        <div>
          <p className={classes.genre}>
            <span itemProp="servesCuisine">{genre}</span>
            {ikyu && isCasual && <span>／カジュアル</span>}
          </p>
          <h1 itemProp="name" className={classes.restaurantName}>
            {restaurantName}
          </h1>
        </div>
        <div className={classes.subtitle}>
          <div className={classes.supplement}>
            <div className={vstack({ gap: '1', alignItems: 'start', width: 'full' })}>
              {accessText && (
                <Link
                  className={classes.access}
                  to={`/${restaurantId}/access`}
                  prefetch="viewport"
                  preventScrollReset={true}
                >
                  <p className={classes.accessText}>{accessText}</p>
                  <ChevronRightOutlineIcon className={classes.chevron} />
                </Link>
              )}
              <PriceRange restaurant={restaurant} size={isPC ? 'lg' : 'sm'} />
            </div>
            <div className={hstack({ gap: '2' })}>
              <GuideIkyuDayBanner show={showIkyuDayBanner} largeAreaCode={largeCode} />
              {visibleTimeSaleLabel && (
                <LabelNormal text="タイムセール" className={classes.timesale}>
                  <TimerOutlineIcon width={12} height={12} className={classes.timesaleIcon} />
                </LabelNormal>
              )}
            </div>
          </div>
          <div className={classes.buttons}>
            {showInventoryNotification && (
              <InventoryNotificationComponent restaurantId={restaurantId} />
            )}
            <ShareRestaurantButton restaurant={restaurant} />
            <FavoriteRestaurantButton restaurantId={restaurantId} size={isPC ? 'md' : 'sm'} />
          </div>
        </div>
      </div>
    </div>
  )
}
